import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconCalendarEvent,
  IconPlus,
  IconReportMedical,
} from "@tabler/icons-react";
import { notifyError, notifySuccess } from "../../utils/notify";
import {
  AssignFormPayload,
  ClientFormSummary,
  FormTemplate,
} from "../../types/formTemplates";
import { useClientForms } from "../../context/ClientFormsContext";
import { useNavigationLoading } from "../../context/NavigationLoadingContext";
import { useSalon } from "../../context/SalonContext";
import { TemplateLibraryModal } from "../formTemplates/TemplateLibraryModal";
import { FormViewer } from "../formTemplates/viewer/FormViewer";
import { formatDateTime } from "../../utils/timeUtils";
import { clientFormStatusTag } from "../../utils/clientFormStatus";
import { BusinessInfo, SalonContextType } from "../../types/context";
import primaryAction from "../common/primaryFilledButton.module.css";
import styles from "./ClientSoapNotesTab.module.css";

interface ClientSoapNotesTabProps {
  salonId: string;
  clientId: string;
  readOnly?: boolean;
}

type ViewerState = { mode: "closed" } | { mode: "open"; formId: string };

const ROW_DATE_FORMAT = "MMM D, YYYY h:mm A";

function normalizeList(value: unknown): ClientFormSummary[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.forms)) return record.forms as ClientFormSummary[];
  }
  return [];
}

export const ClientSoapNotesTab: React.FC<ClientSoapNotesTabProps> = ({
  salonId,
  clientId,
  readOnly = false,
}) => {
  const { listClientForms, assignForm } = useClientForms();
  const navigate = useNavigate();
  const { showNavigationLoading } = useNavigationLoading();
  const { businessInfo } = useSalon() as SalonContextType;
  const timezone =
    (businessInfo as BusinessInfo | null)?.timezone || "America/New_York";

  const [notes, setNotes] = useState<ClientFormSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [viewer, setViewer] = useState<ViewerState>({ mode: "closed" });

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setIsLoading(true);
    try {
      const list = await listClientForms(clientId, {
        category: "per_visit_clinical",
      });
      setNotes(normalizeList(list));
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not load SOAP notes",
      });
    } finally {
      setIsLoading(false);
    }
  }, [clientId, listClientForms]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openAppointment = (transactionUuid: string, apptLabel: string) => {
    showNavigationLoading(
      "Opening appointment",
      `Loading appointment on ${apptLabel}…`,
    );
    navigate("/admin/calendar", {
      state: { targetTransactionId: transactionUuid },
    });
  };

  const handleAssign = async (template: FormTemplate): Promise<boolean> => {
    setIsAssigning(true);
    try {
      const payload: AssignFormPayload = {
        source_type:
          template.source === "custom" ? "salon_template" : "template",
        source_id: template.slug || template.id,
      };
      const assigned = await assignForm(clientId, payload);
      notifySuccess(`${assigned.name} is now on this client.`, {
        title: "SOAP note added",
      });
      await refresh();
      // Open the new note immediately so staff can start writing.
      setViewer({ mode: "open", formId: assigned.id });
      return true;
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not add SOAP note",
      });
      return false;
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUseTemplate = async (template: FormTemplate) => {
    const ok = await handleAssign(template);
    if (ok) setLibraryOpen(false);
  };

  const canAdd = !readOnly;

  return (
    <Box className={styles.shell}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Stack gap={4}>
          <Title order={2} className={styles.title}>
            SOAP Notes
          </Title>
          <Text className={styles.subtitle}>
            Clinical notes per appointment, plus any standalone notes added
            directly to this client.
          </Text>
        </Stack>
        <Button
          variant="filled"
          className={primaryAction.primaryFilled}
          leftSection={<IconPlus size={16} stroke={1.75} />}
          onClick={() => setLibraryOpen(true)}
          disabled={!canAdd}
        >
          Add SOAP Note
        </Button>
      </Group>

      {isLoading && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      )}

      {!isLoading && notes.length === 0 && (
        <Box className={styles.emptyState}>
          <IconReportMedical size={32} stroke={1.5} />
          <Text fw={600} mt="sm">
            No SOAP notes yet
          </Text>
          <Text c="dimmed" size="sm">
            Click <strong>Add SOAP Note</strong> to record one directly on this
            client, or add one from an appointment to link it to that visit.
          </Text>
        </Box>
      )}

      {!isLoading && notes.length > 0 && (
        <Stack gap="sm" mt="md">
          {notes.map((note) => {
            const linked = note.appointments?.[0];
            // Three states for the date column + Open-appointment affordance:
            //   1. Linked appointment with a scheduled start_dt
            //      → render appointment date/time, show Open appointment.
            //   2. Linked appointment without a start_dt (no booked services
            //      yet) → show "Unscheduled appointment", hide the button.
            //      CalendarView.handleNavigationToTransaction throws when a
            //      transaction has no booked_services[0], so opening such an
            //      appointment from here would flash the overlay and fail
            //      silently.
            //   3. No linked appointment (standalone client note)
            //      → fall back to the note's own created_dt label.
            const hasLinkedAppointment = Boolean(linked?.transaction_uuid);
            const hasScheduledStart = Boolean(linked?.start_dt);
            const canOpenAppointment =
              hasLinkedAppointment && hasScheduledStart;
            const apptLabel = hasLinkedAppointment
              ? hasScheduledStart
                ? formatDateTime(linked!.start_dt!, timezone, ROW_DATE_FORMAT)
                : "Unscheduled appointment"
              : note.created_dt
                ? formatDateTime(note.created_dt, timezone, ROW_DATE_FORMAT)
                : "Date unknown";
            const badge = clientFormStatusTag(note.status);
            return (
              <Card
                key={note.id}
                withBorder
                radius="md"
                padding="md"
                className={styles.row}
              >
                <Group justify="space-between" wrap="nowrap" align="center">
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" align="center">
                      <Text fw={600} size="sm" truncate>
                        {note.custom_name?.trim() || note.name}
                      </Text>
                      <Badge size="xs" color={badge.color} variant="light">
                        {badge.label}
                      </Badge>
                    </Group>
                    <Group gap="xs" align="center" c="dimmed">
                      <IconCalendarEvent size={14} />
                      <Text size="xs">{apptLabel}</Text>
                    </Group>
                  </Stack>
                  <Group gap="xs" wrap="nowrap">
                    {canOpenAppointment && (
                      <Button
                        size="xs"
                        variant="outline"
                        color="var(--brand-teal)"
                        onClick={() =>
                          openAppointment(linked!.transaction_uuid, apptLabel)
                        }
                      >
                        Open appointment
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="filled"
                      className={primaryAction.primaryFilled}
                      onClick={() =>
                        setViewer({ mode: "open", formId: note.id })
                      }
                    >
                      View note
                    </Button>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      <TemplateLibraryModal
        opened={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onUseTemplate={handleUseTemplate}
        onEditCustom={() => {
          // Editing a note template from the SOAP-add flow is intentionally
          // unsupported — keep the picker focused on assigning to the client.
          // Custom-template management still lives in Settings.
        }}
        onCreateNew={() => {
          // Scratch-build for `per_visit_clinical` not wired up yet (FormBuilder
          // defaults category='reusable'); button is hidden in the modal via
          // categoryFilter.
        }}
        canManageTemplates={false}
        canManageClientForms={canAdd}
        isAssigning={isAssigning}
        categoryFilter="per_visit_clinical"
      />

      {viewer.mode === "open" && (
        <FormViewer
          salonId={salonId}
          clientId={clientId}
          formId={viewer.formId}
          onClose={() => setViewer({ mode: "closed" })}
          onChanged={() => {
            refresh();
          }}
        />
      )}
    </Box>
  );
};

export default ClientSoapNotesTab;
